import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { API_BASE } from '../lib/api'
import '../styles/ui.css'
import '../styles/reports.css'

// ── API types ─────────────────────────────────────────────────────────

type RpRecipient = {
    id?: string
    email: string
    displayName: string | null
}

type RpSchedule = {
    id: string | null
    reportKind: string
    reportLabel: string
    storeId: string
    storeNumber: string
    storeName: string
    enabled: boolean
    dailyTime: string
    nextRunAt: string | null
    lastRunAt: string | null
    lastCheckedAt: string | null
    lastError: string | null
    recipients: RpRecipient[]
}

type RpSchedulesResponse = {
    serverTimeZone: string
    serverTimeZoneName: string
    serverUtcOffsetMinutes: number
    kinds: { kind: string; label: string }[]
    rows: RpSchedule[]
}

type RpLogKind = 'Sent' | 'Test' | 'Error' | 'Suppressed' | 'Skipped' | 'Stalled' | 'Started'

type RpLogEntry = {
    id: string
    reportKind: string
    storeId: string | null
    kind: RpLogKind
    detail: string
    recipients: string | null
    createdAt: string
}

type RpHealth = {
    lastTickAt: string | null
    isStale: boolean
    staleAfterMinutes: number
    automatedEnabled: boolean
}

// Filter presets for the activity log. "Problems" is the one that matters when email itself is
// broken — an alert about a mail failure can't arrive by mail, so this is the fallback channel.
const LOG_FILTERS: { label: string; kinds: string | null }[] = [
    { label: 'All activity', kinds: null },
    { label: 'Problems', kinds: 'Error,Stalled' },
    { label: 'Sent', kinds: 'Sent' },
    { label: 'Nothing to send', kinds: 'Skipped' },
    { label: 'Paused', kinds: 'Suppressed' },
    { label: 'Tests', kinds: 'Test' },
    { label: 'Scheduler', kinds: 'Started,Stalled' },
]

// ── Helpers ───────────────────────────────────────────────────────────

const fmtWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

// Human label for the server's timezone, noting when it differs from the browser's.
const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone } catch { return null } })()
const serverTzLabel = (name: string | null, id: string | null, serverOffsetMin: number | null | undefined) => {
    const label = name || id || "the server's local time zone"
    // JS getTimezoneOffset is minutes behind UTC (inverted), so negate to get the true offset.
    const browserOffset = -new Date().getTimezoneOffset()
    if (browserTz && serverOffsetMin != null && serverOffsetMin !== browserOffset)
        return `${label} — different from your browser (${browserTz})`
    return label
}

const DAILY_SALES = 'daily-sales'

// ── Page ──────────────────────────────────────────────────────────────

export default function ReportsPage() {
    const token = useAuthStore(s => s.accessToken)
    const user = useAuthStore(s => s.user)
    const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token])
    const canManage = user?.permissions?.includes('reports.manage') ?? false

    const [editing, setEditing] = useState<RpSchedule | null>(null)
    const [fullLogOpen, setFullLogOpen] = useState(false)
    const [logFilter, setLogFilter] = useState(0)

    const { data, isLoading, error } = useQuery<RpSchedulesResponse>({
        queryKey: ['reports-schedules'],
        queryFn: async () => {
            const res = await fetch(API_BASE + '/api/reports/schedules', { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    const activeFilter = LOG_FILTERS[logFilter]

    const { data: log } = useQuery<RpLogEntry[]>({
        queryKey: ['reports-log', activeFilter.kinds],
        queryFn: async () => {
            const params = new URLSearchParams({ take: '25' })
            if (activeFilter.kinds) params.set('kind', activeFilter.kinds)
            const res = await fetch(API_BASE + `/api/reports/log?${params}`, { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    // Polled so a stalled scheduler surfaces on a page left open, not only on reload.
    const { data: health } = useQuery<RpHealth>({
        queryKey: ['reports-health'],
        queryFn: async () => {
            const res = await fetch(API_BASE + '/api/reports/health', { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
        refetchInterval: 60_000,
    })

    // Group the flat store × kind grid into one card per store.
    const stores = useMemo(() => {
        if (!data) return []
        const byStore = new Map<string, { storeId: string; storeNumber: string; storeName: string; rows: RpSchedule[] }>()
        for (const row of data.rows) {
            const entry = byStore.get(row.storeId) ?? {
                storeId: row.storeId, storeNumber: row.storeNumber, storeName: row.storeName, rows: [],
            }
            entry.rows.push(row)
            byStore.set(row.storeId, entry)
        }
        return [...byStore.values()]
    }, [data])

    return (
        <div className="rp-page">
            <div className="rp-header">
                <div>
                    <h1 className="rp-title">Scheduled reports</h1>
                    <p className="rp-subtitle">
                        Daily sales PDFs and PO lockdown reminders, emailed per store.
                        {data && <> Send times are in {serverTzLabel(data.serverTimeZoneName, data.serverTimeZone, data.serverUtcOffsetMinutes)}.</>}
                    </p>
                </div>
                <button className="rp-link-btn" onClick={() => setFullLogOpen(true)}>View full activity log →</button>
            </div>

            {health && <SchedulerHealthBanner health={health} />}

            {error && <div className="rp-error-banner">Couldn't load schedules: {(error as Error).message}</div>}

            {isLoading ? (
                <div className="rp-note">Loading…</div>
            ) : stores.length === 0 ? (
                <div className="empty-state">
                    <p>No stores are available to you yet.</p>
                </div>
            ) : (
                <div className="rp-store-grid">
                    {stores.map(store => (
                        <div key={store.storeId} className="rp-store-card">
                            <div className="rp-store-head">
                                <span className="rp-store-name">{store.storeName}</span>
                                <span className="rp-store-number">#{store.storeNumber}</span>
                            </div>
                            {store.rows.map(row => (
                                <ScheduleRow
                                    key={`${row.reportKind}-${row.storeId}`}
                                    row={row}
                                    canManage={canManage}
                                    onEdit={() => setEditing(row)}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}

            <div className="rp-activity">
                <div className="rp-activity-head">
                    <h2 className="rp-section-title">Recent activity</h2>
                    <div className="rp-filter-row">
                        {LOG_FILTERS.map((f, i) => (
                            <button
                                key={f.label}
                                className={`rp-filter-pill${i === logFilter ? ' active' : ''}`}
                                onClick={() => setLogFilter(i)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
                {!log || log.length === 0 ? (
                    <div className="rp-note">
                        {activeFilter.kinds
                            ? `No "${activeFilter.label.toLowerCase()}" events recorded.`
                            : 'No report activity recorded yet.'}
                    </div>
                ) : (
                    <div className="rp-log-list">
                        {log.map(entry => (
                            <div key={entry.id} className={`rp-log-entry kind-${entry.kind}`}>
                                <strong>{new Date(entry.createdAt).toLocaleString()}</strong> · {entry.kind} — {entry.detail}
                                {entry.recipients && <span> → {entry.recipients}</span>}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {editing && (
                <ScheduleSettingsModal
                    row={editing}
                    canManage={canManage}
                    authHeaders={authHeaders}
                    tzLabel={data ? serverTzLabel(data.serverTimeZoneName, data.serverTimeZone, data.serverUtcOffsetMinutes) : ''}
                    onClose={() => setEditing(null)}
                />
            )}

            {fullLogOpen && <FullLogModal authHeaders={authHeaders} onClose={() => setFullLogOpen(false)} />}
        </div>
    )
}

// ── Scheduler liveness ────────────────────────────────────────────────

// The log only records runs that were DUE, so a paused scheduler and a worker process that IIS
// idled out overnight produce identical silence. This is the signal that tells them apart.
function SchedulerHealthBanner({ health }: { health: RpHealth }) {
    const since = health.lastTickAt
        ? Math.round((Date.now() - new Date(health.lastTickAt).getTime()) / 60000)
        : null

    if (health.isStale) {
        return (
            <div className="rp-health-banner stale">
                <strong>The report scheduler isn't running.</strong>{' '}
                {health.lastTickAt
                    ? <>It last checked {since} minute{since === 1 ? '' : 's'} ago ({fmtWhen(health.lastTickAt)}); it should check every minute.</>
                    : <>It has not checked in since the API started.</>}{' '}
                Scheduled reports won't go out until it does. This is usually the API's IIS
                application pool idling out — it needs Start Mode "AlwaysRunning" and an idle
                time-out of 0.
            </div>
        )
    }

    return (
        <div className="rp-health-banner ok">
            Scheduler is running — last checked {since === 0 ? 'less than a minute' : `${since} minute${since === 1 ? '' : 's'}`} ago.
            {!health.automatedEnabled && (
                <> Scheduled sends are <strong>paused</strong>; checks still run and are logged, and Send test / Run now still work.</>
            )}
        </div>
    )
}

// ── One report kind for one store ─────────────────────────────────────

function ScheduleRow({ row, canManage, onEdit }: {
    row: RpSchedule
    canManage: boolean
    onEdit: () => void
}) {
    return (
        <div className="rp-schedule-row">
            <div className="rp-schedule-main">
                <span className={`rp-status-pill ${row.enabled ? 'on' : 'off'}`}>
                    {row.enabled ? 'On' : 'Off'}
                </span>
                <span className="rp-schedule-label">{row.reportLabel}</span>
                {row.lastError && <span className="rp-warn-chip" title={row.lastError}>⚠ Last run failed</span>}
            </div>
            <div className="rp-schedule-meta">
                {row.enabled ? (
                    <>Daily at {row.dailyTime} · next {fmtWhen(row.nextRunAt)} · {row.recipients.length} recipient{row.recipients.length === 1 ? '' : 's'}</>
                ) : (
                    <>Not scheduled</>
                )}
            </div>
            <button className="btn btn-secondary rp-row-btn" onClick={onEdit}>
                {canManage ? 'Settings' : 'View'}
            </button>
        </div>
    )
}

// ── Settings modal ────────────────────────────────────────────────────

function ScheduleSettingsModal({ row, canManage, authHeaders, tzLabel, onClose }: {
    row: RpSchedule
    canManage: boolean
    authHeaders: Record<string, string>
    tzLabel: string
    onClose: () => void
}) {
    const qc = useQueryClient()
    const [enabled, setEnabled] = useState(row.enabled)
    const [dailyTime, setDailyTime] = useState(row.dailyTime)
    const [recipients, setRecipients] = useState<RpRecipient[]>(row.recipients)
    const [newEmail, setNewEmail] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [notice, setNotice] = useState<string | null>(null)

    const { data: log } = useQuery<RpLogEntry[]>({
        queryKey: ['reports-log', row.reportKind, row.storeId],
        queryFn: async () => {
            const params = new URLSearchParams({ reportKind: row.reportKind, storeId: row.storeId, take: '15' })
            const res = await fetch(API_BASE + `/api/reports/log?${params}`, { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    useEffect(() => {
        setEnabled(row.enabled)
        setDailyTime(row.dailyTime)
        setRecipients(row.recipients)
    }, [row])

    const addRecipient = () => {
        const email = newEmail.trim()
        if (!email.includes('@')) { setError('Enter a valid email address.'); return }
        if (recipients.some(r => r.email.toLowerCase() === email.toLowerCase())) { setNewEmail(''); return }
        setRecipients(prev => [...prev, { email, displayName: null }])
        setNewEmail('')
        setError(null)
    }

    const save = useMutation({
        mutationFn: async () => {
            const res = await fetch(API_BASE + '/api/reports/schedules', {
                method: 'PUT',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reportKind: row.reportKind,
                    storeId: row.storeId,
                    enabled,
                    dailyTime,
                    recipients: recipients.map(r => ({ email: r.email, displayName: r.displayName })),
                }),
            })
            const json = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(json.message ?? json.error ?? 'Failed to save the schedule.')
            return json as { nextRunAt?: string }
        },
        onSuccess: json => {
            setError(null)
            setNotice(`Saved.${json?.nextRunAt ? ` Next run: ${fmtWhen(json.nextRunAt)}.` : ''}`)
            qc.invalidateQueries({ queryKey: ['reports-schedules'] })
        },
        onError: (e: Error) => { setNotice(null); setError(e.message) },
    })

    const action = useMutation({
        mutationFn: async (kind: 'test' | 'run') => {
            const res = await fetch(API_BASE + `/api/reports/schedules/${row.id}/${kind}`, {
                method: 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: kind === 'test' ? JSON.stringify({ emails: [] }) : undefined,
            })
            const json = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(json.message ?? json.detail ?? json.error ?? 'Request failed.')
            return json as { message?: string }
        },
        onSuccess: json => {
            setError(null)
            setNotice(json?.message ?? 'Done.')
            qc.invalidateQueries({ queryKey: ['reports-log'] })
            qc.invalidateQueries({ queryKey: ['reports-schedules'] })
        },
        onError: (e: Error) => { setNotice(null); setError(e.message) },
    })

    const previewPdf = async () => {
        setError(null)
        setNotice('Generating the PDF…')
        try {
            const res = await fetch(API_BASE + `/api/reports/daily-sales/pdf?storeId=${row.storeId}`, { headers: authHeaders })
            if (!res.ok) {
                const json = await res.json().catch(() => ({}))
                throw new Error(json.detail ?? json.message ?? 'The report could not be generated.')
            }
            const blob = await res.blob()
            window.open(URL.createObjectURL(blob), '_blank', 'noopener')
            setNotice('Opened in a new tab.')
        } catch (e) {
            setNotice(null)
            setError((e as Error).message)
        }
    }

    const unsaved = row.id === null

    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 580 }}>
                <div className="modal-header">
                    <h2>{row.reportLabel} — {row.storeName}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <label className="rp-toggle-label">
                        <input type="checkbox" checked={enabled} disabled={!canManage}
                               onChange={e => setEnabled(e.target.checked)} />
                        Send this report for {row.storeName}
                    </label>

                    <div className="form-field">
                        <label className="form-label">Send daily at</label>
                        <input className="form-input" style={{ width: 150 }} type="time" step={60}
                               value={dailyTime} disabled={!canManage}
                               onChange={e => setDailyTime(e.target.value)} />
                        <p className="rp-note" style={{ margin: 'var(--space-1) 0 0' }}>
                            Times are in {tzLabel}.
                        </p>
                    </div>

                    <div className="form-field">
                        <label className="form-label">Recipients</label>
                        {recipients.length === 0 && (
                            <p className="rp-note" style={{ margin: '0 0 var(--space-2)' }}>
                                No recipients yet — this report can't be sent until you add one.
                            </p>
                        )}
                        {recipients.map(r => (
                            <div key={r.email} className="rp-recipient-row">
                                <span>{r.email}</span>
                                {canManage && (
                                    <button className="btn-icon" title="Remove recipient"
                                            onClick={() => setRecipients(prev => prev.filter(x => x.email !== r.email))}>🗑️</button>
                                )}
                            </div>
                        ))}
                        {canManage && (
                            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                                <input className="form-input" style={{ flex: 1, minWidth: 220 }} type="email"
                                       placeholder="Add an email address" value={newEmail}
                                       onChange={e => setNewEmail(e.target.value)}
                                       onKeyDown={e => { if (e.key === 'Enter') addRecipient() }} />
                                <button className="btn btn-secondary" onClick={addRecipient}>Add</button>
                            </div>
                        )}
                    </div>

                    <div className="rp-note" style={{ marginBottom: 'var(--space-3)' }}>
                        {/* Last checked is deliberately separate from last run: on a paused
                            schedule, or one with nothing to send, they diverge — and that gap is
                            the evidence that the scheduler is alive and doing its job. */}
                        Last checked: {fmtWhen(row.lastCheckedAt)} · Last sent: {fmtWhen(row.lastRunAt)} · Next check: {fmtWhen(row.nextRunAt)}
                    </div>
                    {row.lastError && <div className="rp-error-banner">Last run failed: {row.lastError}</div>}

                    {log && log.length > 0 && (
                        <div className="form-field">
                            <label className="form-label">Recent activity</label>
                            <div className="rp-log-list">
                                {log.map(entry => (
                                    <div key={entry.id} className={`rp-log-entry kind-${entry.kind}`}>
                                        <strong>{new Date(entry.createdAt).toLocaleString()}</strong> — {entry.detail}
                                        {entry.recipients && <span> → {entry.recipients}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {notice && <div className="rp-notice">{notice}</div>}
                    {error && <div className="rp-error-banner">{error}</div>}
                </div>
                <div className="modal-footer">
                    {canManage && (
                        <>
                            <button className="btn btn-secondary" title={unsaved ? 'Save this schedule first.' : undefined}
                                    onClick={() => action.mutate('test')} disabled={action.isPending || unsaved}>
                                Send test
                            </button>
                            <button className="btn btn-secondary" title={unsaved ? 'Save this schedule first.' : undefined}
                                    onClick={() => action.mutate('run')} disabled={action.isPending || unsaved || !enabled}>
                                Run now
                            </button>
                        </>
                    )}
                    {row.reportKind === DAILY_SALES && (
                        <button className="btn btn-secondary" onClick={previewPdf}>Preview PDF</button>
                    )}
                    <span style={{ flex: 1 }} />
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                    {canManage && (
                        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
                            {save.isPending ? 'Saving…' : 'Save'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

// ── Full activity log ─────────────────────────────────────────────────

function FullLogModal({ authHeaders, onClose }: {
    authHeaders: Record<string, string>
    onClose: () => void
}) {
    const [filter, setFilter] = useState(0)
    const active = LOG_FILTERS[filter]

    const { data: log, isLoading } = useQuery<RpLogEntry[]>({
        queryKey: ['reports-fulllog', active.kinds],
        queryFn: async () => {
            const params = new URLSearchParams({ take: '200' })
            if (active.kinds) params.set('kind', active.kinds)
            const res = await fetch(API_BASE + `/api/reports/log?${params}`, { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    return (
        <div className="modal-backdrop" style={{ zIndex: 60 }}>
            <div className="modal" style={{ maxWidth: 720 }}>
                <div className="modal-header">
                    <h2>Report activity log</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <div className="rp-filter-row" style={{ marginBottom: 'var(--space-3)' }}>
                        {LOG_FILTERS.map((f, i) => (
                            <button
                                key={f.label}
                                className={`rp-filter-pill${i === filter ? ' active' : ''}`}
                                onClick={() => setFilter(i)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    {isLoading ? (
                        <div className="rp-note">Loading…</div>
                    ) : !log || log.length === 0 ? (
                        <div className="rp-note">
                            {active.kinds
                                ? `No "${active.label.toLowerCase()}" events recorded.`
                                : 'No report activity recorded yet.'}
                        </div>
                    ) : (
                        <>
                            <p className="rp-note" style={{ marginTop: 0 }}>
                                Showing the {log.length} most recent event{log.length === 1 ? '' : 's'}.
                            </p>
                            <div className="rp-log-list" style={{ maxHeight: '55vh' }}>
                                {log.map(entry => (
                                    <div key={entry.id} className={`rp-log-entry kind-${entry.kind}`}>
                                        <strong>{new Date(entry.createdAt).toLocaleString()}</strong> · {entry.reportKind} · {entry.kind} — {entry.detail}
                                        {entry.recipients && <span> → {entry.recipients}</span>}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    )
}
